/** TanStack Query hooks for Projects: list, detail by id or slug, create, update */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { CreateProjectInput, ProjectSummary, UpdateProjectInput } from '@agentwiki/shared'
import { apiClient, ApiError } from '../lib/api-client'

export const projectKeys = {
  all: ['projects'] as const,
  list: () => [...projectKeys.all, 'list'] as const,
  detail: (idOrSlug: string) => [...projectKeys.all, 'detail', idOrSlug] as const,
}

export function useProjects() {
  return useQuery<ProjectSummary[], ApiError>({
    queryKey: projectKeys.list(),
    queryFn: () => apiClient.get<{ projects: ProjectSummary[] }>('/api/projects').then((r) => r.projects),
  })
}

export function useProject(idOrSlug: string | undefined) {
  return useQuery<ProjectSummary, ApiError>({
    queryKey: projectKeys.detail(idOrSlug ?? ''),
    queryFn: () => apiClient.get<ProjectSummary>(`/api/projects/${encodeURIComponent(idOrSlug ?? '')}`),
    enabled: !!idOrSlug,
    // A missing project will not appear on retry
    retry: (count, err) => err.status !== 404 && count < 1,
  })
}

export function useCreateProject() {
  const qc = useQueryClient()
  return useMutation<ProjectSummary, ApiError, CreateProjectInput>({
    mutationFn: (input) => apiClient.post<ProjectSummary>('/api/projects', input),
    onSuccess: (project) => {
      qc.setQueryData(projectKeys.detail(project.slug), project)
      return qc.invalidateQueries({ queryKey: projectKeys.list() })
    },
  })
}

/** PATCH /api/projects/:project — name, description, repository URL (the slug never changes) */
export function useUpdateProject() {
  const qc = useQueryClient()
  return useMutation<ProjectSummary, ApiError, { ref: string; input: UpdateProjectInput }>({
    mutationFn: ({ ref, input }) => apiClient.patch<ProjectSummary>(`/api/projects/${encodeURIComponent(ref)}`, input),
    onSuccess: (project) => {
      qc.setQueryData(projectKeys.detail(project.slug), project)
      return qc.invalidateQueries({ queryKey: projectKeys.all })
    },
  })
}

/** Machine-readable error code from an API error body, e.g. PROJECT_SLUG_TAKEN */
export function apiErrorCode(err: unknown): string | undefined {
  if (!(err instanceof ApiError) || typeof err.data !== 'object' || err.data === null) return undefined
  const code = (err.data as Record<string, unknown>).code
  return typeof code === 'string' ? code : undefined
}
